/**
 * Taking a part from the shop briefing onto a line, and taking it back off.
 *
 * The briefing suggests; it never orders. Nothing here runs on its own — both
 * functions are called from a button the advisor presses, and both return a new
 * array for the table to hand to `onChange`. Kept out of the table component so
 * the rule stays testable without rendering the table.
 */

import type { ShopNotePart, LinePartRef } from './ShopNotesPanel';

/** The shape the estimate stores. Loose because callers hold several line types. */
interface HasParts {
  parts?: any[];
}

/** Round to cents. A float total reaches the customer's invoice as-is. */
const money = (n: number): number => Math.round(n * 100) / 100;

/**
 * Append a suggested part to one line.
 *
 * The line is matched by identity rather than by id: an unsaved line has no id
 * yet, and the table is already holding the exact object the advisor clicked.
 */
export function addSuggestedPart<T extends HasParts>(
  items: T[],
  line: T,
  suggested: Pick<ShopNotePart, 'name' | 'partNumber' | 'priceUsd'> & {
    quantity?: number | null;
    why?: string | null;
  }
): T[] {
  const quantity = suggested.quantity ?? 1;
  // The vendor does not price every consumable. Zero shows as missing on the line;
  // a guessed price would flow silently into the customer's total.
  const unitPrice = suggested.priceUsd ?? 0;

  return items.map((item) =>
    item === line
      ? {
          ...item,
          parts: [
            ...(item.parts ?? []),
            {
              part_number: suggested.partNumber ?? '',
              description: suggested.name,
              quantity,
              unit_price: money(unitPrice),
              total_price: money(unitPrice * quantity),
              availability: 'order_required',
              // Why it was suggested, for whoever places the order.
              notes: suggested.why ?? null,
            },
          ],
        }
      : item
  );
}

/** Take a part back off the line it was added to, leaving every other line alone. */
export function removeSuggestedPart<T extends HasParts>(
  items: T[],
  line: T,
  onLine: LinePartRef
): T[] {
  return items.map((item) =>
    item === line
      ? {
          ...item,
          // Half the consumables the vendor names — coolant, RTV, an O-ring — carry
          // no part number, so fall back to the description for those.
          parts: (item.parts ?? []).filter((p: any) =>
            onLine.part_number
              ? p.part_number !== onLine.part_number
              : p.description !== onLine.description
          ),
        }
      : item
  );
}

/**
 * A work-order part row, as that side of the app stores it.
 *
 * Work orders keep a line's parts as their own rows behind their own endpoints,
 * and call the name `part_name` where the estimate calls it `description`. Only
 * the fields this translation touches are named here.
 */
export interface WorkOrderPartRow {
  id?: number;
  part_name?: string | null;
  part_number?: string | null;
}

/** Build the POST body that adds a suggested part to a work-order line. */
export function suggestedPartToWorkOrderPart(
  suggested: Pick<ShopNotePart, 'name' | 'partNumber' | 'priceUsd'> & {
    quantity?: number | null;
    why?: string | null;
  }
) {
  const quantity = suggested.quantity ?? 1;
  const unitPrice = suggested.priceUsd ?? 0;
  return {
    part_name: suggested.name,
    part_number: suggested.partNumber ?? '',
    quantity,
    unit_price: money(unitPrice),
    cost: null as number | null,
    // No discount of its own: whatever the job carries is applied by the work
    // order, and a second one here would compound it.
    discount_percentage: 0,
    discount_amount: 0,
    discounted_price: money(unitPrice * quantity),
    total_price: money(unitPrice * quantity),
    // Just accepted, so not yet ordered. Any later status would hide it from
    // the parts-to-order list.
    status: 'needed' as const,
    supplier: '',
    tracking_number: '',
    expected_delivery: '',
    notes: suggested.why ?? '',
  };
}

/**
 * Read a stored work-order part back as the reference the panel matches on.
 *
 * Without this the panel compares its suggestions against `description`, finds
 * nothing on a work-order line, and offers to add every part a second time.
 */
export function workOrderPartToLineRef(part: WorkOrderPartRow): LinePartRef {
  return {
    id: part.id,
    part_number: part.part_number ?? null,
    description: part.part_name ?? null,
  };
}
